import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './config/firebase';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import DashboardPage from './pages/DashboardPage';
import PostJobPage from './pages/PostJobPage';
import ManageJobsPage from './pages/ManageJobsPage';
import ChatListPage from './pages/ChatListPage';
import ChatRoomPage from './pages/ChatRoomPage';
import ViewApplicantsPage from './pages/ViewApplicantsPage';
import AllApplicantsPage, { AllHiredPage } from './pages/AllApplicantsPage';
import PaymentSetupPage from './pages/PaymentSetupPage';
import ProfilePage from './pages/ProfilePage';
import I9SchedulePage from './pages/I9SchedulePage';
import EditJobPage from './pages/EditJobPage';
import OfferLettersPage from './pages/OfferLettersPage';
import CompanyProfilePage from './pages/CompanyProfilePage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import AppShell from './components/AppShell';


function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Skip role check during registration and email verification.
      // The Firestore doc may not exist yet (onUserCreated trigger hasn't
      // fired) or may have role: candidate (trigger beat the client write).
      // Role enforcement only matters for protecting dashboard routes.
      const path = window.location.pathname;
      if (path === '/register' || path === '/verify-email') {
        setUser(currentUser);
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        const role = snap.data()?.role;
        if (role !== 'employer' && role !== 'admin') {
          try { await signOut(auth); } catch {}
          setUser(null);
          setLoading(false);
          return;
        }
        setUser(currentUser);
      } catch {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f3f4f6'
      }}>
        <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading...</div>
      </div>
    );
  }

  const ProtectedRoute = ({ children }) => {
    if (!user) return <Navigate to="/login" replace />;
    return children;
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><AppShell><DashboardPage /></AppShell></ProtectedRoute>} />
        <Route path="/post-job" element={<ProtectedRoute><AppShell><PostJobPage /></AppShell></ProtectedRoute>} />
        <Route path="/manage-jobs" element={<ProtectedRoute><AppShell><ManageJobsPage /></AppShell></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><AppShell><ChatListPage /></AppShell></ProtectedRoute>} />
        <Route path="/chat/:conversationId" element={<ProtectedRoute><AppShell><ChatRoomPage /></AppShell></ProtectedRoute>} />
        <Route path="/jobs/:jobId/applicants" element={<ProtectedRoute><AppShell><ViewApplicantsPage /></AppShell></ProtectedRoute>} />
        <Route path="/applicants" element={<ProtectedRoute><AppShell><AllApplicantsPage /></AppShell></ProtectedRoute>} />
        <Route path="/hired" element={<ProtectedRoute><AppShell><AllHiredPage /></AppShell></ProtectedRoute>} />
        <Route path="/payment-setup" element={<ProtectedRoute><AppShell><PaymentSetupPage /></AppShell></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><AppShell><ProfilePage /></AppShell></ProtectedRoute>} />
        <Route path="/i9-schedule" element={<ProtectedRoute><AppShell><I9SchedulePage /></AppShell></ProtectedRoute>} />
        <Route path="/edit-job/:jobId" element={<ProtectedRoute><AppShell><EditJobPage /></AppShell></ProtectedRoute>} />
        <Route path="/offer-letters" element={<ProtectedRoute><AppShell><OfferLettersPage /></AppShell></ProtectedRoute>} />
        <Route path="/account/profile" element={<ProtectedRoute><AppShell><CompanyProfilePage /></AppShell></ProtectedRoute>} />
        <Route path="/account/settings" element={<ProtectedRoute><AppShell><AccountSettingsPage /></AppShell></ProtectedRoute>} />
      </Routes>
    </Router>
  );
}

export default App;